import axios, { type AxiosInstance } from 'axios';
import type { MerchantEditableFields } from '@mlm/shared';
import { useAuthStore, type AuthUser } from './auth-store';
import { queryClient } from './query-client';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

export const api: AxiosInstance = axios.create({ baseURL: BASE_URL, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Turn a thrown request error into a human message. Axios swallows the server
 * body behind a generic "Request failed with status code 4xx"; this digs out
 * the API's `{ error, message, issues }` so toasts/dialogs show the real reason.
 */
export function apiErrorMessage(e: unknown, fallback = 'Request failed'): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as
      | {
          error?: string;
          message?: string;
          issues?: Array<{ path?: (string | number)[]; message?: string }>;
        }
      | undefined;
    if (data?.issues?.length) {
      return data.issues
        .map((i) => `${(i.path ?? []).join('.') || 'value'}: ${i.message ?? 'invalid'}`)
        .join('; ');
    }
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (e.message) return e.message;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

let refreshInFlight: Promise<string | null> | null = null;
async function refreshTokenOnce(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await axios.post<{ accessToken: string }>(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        );
        return res.data.accessToken;
      } catch {
        return null;
      } finally {
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !String(original.url ?? '').includes('/auth/refresh') &&
      !String(original.url ?? '').includes('/auth/login')
    ) {
      original._retried = true;
      const newToken = await refreshTokenOnce();
      if (newToken) {
        const user = useAuthStore.getState().user;
        if (user) useAuthStore.getState().setSession(newToken, user);
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      useAuthStore.getState().clear();
      queryClient.clear();
    }
    return Promise.reject(error);
  },
);

type MeResponse = {
  id: string;
  email: string;
  fullName: string | null;
  roles: string[];
  perms: string[];
  totpEnabled: boolean;
};

export async function login(args: {
  email: string;
  password: string;
  totp?: string;
}): Promise<AuthUser> {
  const res = await api.post<{ accessToken: string; user: MeResponse }>('/auth/login', args);
  const u = res.data.user;
  const user: AuthUser = {
    id: u.id,
    email: u.email,
    fullName: u.fullName,
    roles: u.roles,
    perms: u.perms,
    totpEnabled: u.totpEnabled,
  };
  useAuthStore.getState().setSession(res.data.accessToken, user);
  return user;
}

/** Restore a session on app boot from the httpOnly refresh cookie. */
export async function restoreSession(): Promise<boolean> {
  const token = await refreshTokenOnce();
  if (!token) return false;
  try {
    const res = await api.get<MeResponse>('/me', { headers: { Authorization: `Bearer ${token}` } });
    const u = res.data;
    useAuthStore.getState().setSession(token, {
      id: u.id,
      email: u.email,
      fullName: u.fullName,
      roles: u.roles,
      perms: u.perms,
      totpEnabled: u.totpEnabled,
    });
    return true;
  } catch {
    return false;
  }
}

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    /* ignore */
  }
  useAuthStore.getState().clear();
  queryClient.clear();
}

// ── domain types ──────────────────────────────────────────────────────────
export type MerchantStatus = 'pending' | 'approved' | 'rejected' | 'suspended';

export type RequirementDef = {
  key: string;
  label: string;
  description?: string;
  required: boolean;
  acceptsFile: boolean;
  fileTypes?: string[];
};

export type RequirementSubmission = {
  id: string;
  requirementKey: string;
  fileKey: string | null;
  textValue: string | null;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
};

export type MerchantMe = {
  merchant: {
    id: string;
    businessName: string;
    contactNumber: string | null;
    contactEmail: string | null;
    businessAddress: Record<string, unknown>;
    pickupAddress: Record<string, unknown>;
    pickupRegionCode: string | null;
    status: MerchantStatus;
    rejectionReason: string | null;
    // The linked account: owner display name (editable as "Owner name") and
    // login email (always read-only).
    ownerName: string | null;
    ownerEmail: string;
  };
  requirements: RequirementDef[];
  submissions: RequirementSubmission[];
  /** Which Profile fields the merchant may edit (admin-controlled). */
  profileEditable: MerchantEditableFields;
};

/** Per-variant shipping overrides (null = inherit the product). No PV/points/vortex for merchants. */
export type MerchantVariantOverrides = {
  weightKg: string | null;
  lengthCm: string | null;
  widthCm: string | null;
  heightCm: string | null;
};
export type MerchantVariant = {
  id: string;
  sku: string;
  name: string;
  attributes: Record<string, string>;
  price: string;
  srp: string | null;
  active: boolean;
  stockQty: number | null;
} & MerchantVariantOverrides;

export type MerchantProduct = {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  detailsHtml: string | null;
  price: string;
  srp: string | null;
  pv: string;
  images: string[];
  weightKg: string | null;
  lengthCm: string | null;
  widthCm: string | null;
  heightCm: string | null;
  platformSold: 'cashier' | 'ecom' | 'both';
  soldTo: 'activated' | 'non_activated' | 'both';
  approvalStatus: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
  platformFeePct: string | null;
  categoryId: string | null;
  stockQty: number | null;
  variants: MerchantVariant[];
  createdAt: string;
};

export type MerchantVariantInput = {
  name: string;
  price: string;
  srp?: string | null;
  attributes?: Record<string, string>;
  stockQty?: number;
} & Partial<MerchantVariantOverrides>;

// ── Variation types + combination matrix (merchants reuse the admin's global types) ──
export type VariationOption = {
  id: string;
  variationTypeId: string;
  code: string;
  label: string;
  sortOrder: number;
  active: boolean;
};
export type VariationType = {
  id: string;
  code: string;
  name: string;
  /** null = global (admin-managed, read-only here); else this merchant owns it. */
  ownerMerchantId: string | null;
  active: boolean;
  sortOrder: number;
  options: VariationOption[];
};
export type ProductVariationSelection = { typeId: string; optionIds: string[] };

export async function listVariationTypes(includeInactive = false): Promise<VariationType[]> {
  const res = await api.get<{ types: VariationType[] }>('/merchant/variation-types', {
    params: includeInactive ? { includeInactive: true } : {},
  });
  return res.data.types;
}
export async function createVariationType(input: {
  code: string;
  name: string;
  sortOrder?: number;
}): Promise<VariationType> {
  return (await api.post<VariationType>('/merchant/variation-types', input)).data;
}
export async function updateVariationType(
  id: string,
  patch: Partial<{ name: string; sortOrder: number; active: boolean }>,
): Promise<VariationType> {
  return (await api.patch<VariationType>(`/merchant/variation-types/${id}`, patch)).data;
}
export async function deleteVariationType(id: string): Promise<void> {
  await api.delete(`/merchant/variation-types/${id}`);
}
export async function addVariationOption(
  typeId: string,
  input: { code: string; label: string; sortOrder?: number },
): Promise<VariationOption> {
  return (await api.post<VariationOption>(`/merchant/variation-types/${typeId}/options`, input))
    .data;
}
export async function updateVariationOption(
  optionId: string,
  patch: Partial<{ label: string; sortOrder: number; active: boolean }>,
): Promise<VariationOption> {
  return (await api.patch<VariationOption>(`/merchant/variation-options/${optionId}`, patch)).data;
}
export async function deleteVariationOption(optionId: string): Promise<void> {
  await api.delete(`/merchant/variation-options/${optionId}`);
}
export async function getProductVariations(
  productId: string,
): Promise<{ selections: ProductVariationSelection[]; variants: MerchantVariant[] }> {
  return (await api.get(`/merchant/products/${productId}/variations`)).data;
}
export async function setProductVariations(
  productId: string,
  selections: ProductVariationSelection[],
): Promise<{ variants: MerchantVariant[] }> {
  return (await api.put(`/merchant/products/${productId}/variations`, { selections })).data;
}

/** Tenant pricing defaults for the merchant product earnings preview. */
export type MerchantPricingDefaults = {
  /** The per-merchant platform fee % (admin-set; tenant default fallback). */
  platformFeePct: string;
  /** When false, the merchant's product form hides the earnings preview. */
  showFeeToMerchant: boolean;
  vatPct: string;
  vatEnabled: boolean;
};
export async function getPricingDefaults(): Promise<MerchantPricingDefaults> {
  return (await api.get<MerchantPricingDefaults>('/merchant/pricing-defaults')).data;
}

// Admin-defined product categories (public list). A merchant picks one for a
// product; the category set itself is managed by admin.
export type Category = { id: string; code: string; name: string; parentId: string | null };
export async function listCategories(): Promise<Category[]> {
  return (await api.get<Category[]>('/categories')).data;
}

// ── Featured Feedback: the merchant pins reviews of their own products to a
// "Featured Feedback" section on their storefront shop page. ──
export type MerchantReview = {
  id: string;
  rating: number;
  comment: string | null;
  hasPhoto: boolean;
  photoCount: number;
  productId: string;
  productName: string;
  reviewerName: string;
  featured: boolean;
  createdAt: string;
};

export async function listMerchantReviews(opts?: {
  rating?: number;
  featuredOnly?: boolean;
}): Promise<MerchantReview[]> {
  const params = new URLSearchParams();
  if (opts?.rating) params.set('rating', String(opts.rating));
  if (opts?.featuredOnly) params.set('featuredOnly', 'true');
  const q = params.toString();
  const res = await api.get<{ reviews: MerchantReview[] }>(`/merchant/reviews${q ? `?${q}` : ''}`);
  return res.data.reviews;
}

export async function setMerchantReviewFeatured(id: string, featured: boolean): Promise<void> {
  await api.post(`/merchant/reviews/${id}/feature`, { featured });
}

export function reviewPhotoUrl(productId: string, reviewId: string, index = 0): string {
  const q = index ? `?i=${index}` : '';
  return `${BASE_URL}/products/${productId}/reviews/${reviewId}/photo${q}`;
}

export type MerchantOrder = {
  id: string;
  code: string;
  status: string;
  subtotal: string;
  shippingFee: string;
  total: string;
  courierId: string | null;
  trackingNumber: string | null;
  createdAt: string;
  /** Buyer name + comma-joined product names, for client-side search. */
  buyerName: string | null;
  productNames: string;
};

export type CourierLabel = {
  id: string;
  label: string;
  logoKey: string | null;
  trackingUrlTemplate: string | null;
};
export async function getCourierLabels(): Promise<CourierLabel[]> {
  return (await api.get<{ couriers: CourierLabel[] }>('/couriers/labels')).data.couriers;
}

/** Public courier-logo endpoint (302-redirects to a presigned image) for <img src>. */
export function courierLogoUrl(courierId: string): string {
  return `${BASE_URL}/shipping/courier-logo/${encodeURIComponent(courierId)}`;
}

export type MerchantOrderItem = {
  id: string;
  productId: string;
  productName: string | null;
  productSku: string | null;
  productImages: string[] | null;
  variantId: string | null;
  variantName: string | null;
  qty: number;
  unitPrice: string;
};

export type MerchantOrderDetail = MerchantOrder & {
  shipToAddress: Record<string, unknown> | null;
  buyerFullName: string | null;
  buyerPhone: string | null;
  items: MerchantOrderItem[];
};

export type MerchantWallet = {
  available: string;
  pending: string;
  lifetimeEarned: string;
};

export type MerchantLedgerBreakdown = { gross: string; fee: string; vat: string; net: string };
export type MerchantLedgerRow = {
  id: string;
  kind: string;
  amount: string;
  direction: 'C' | 'D';
  createdAt: string;
  orderCode?: string | null;
  metadata?: { order_id?: string; breakdown?: MerchantLedgerBreakdown } | null;
};

export type PayoutMethod = {
  code: string;
  label: string;
  accountLabel: string;
  accountPlaceholder: string;
};

export type MerchantPayoutRequest = {
  id: string;
  amount: string;
  netAmount: string;
  method: string;
  status: string;
  rejectionReason: string | null;
  requestedAt: string;
};

export type ShippingZone = { id: string; label: string };

/** Public shipping zones for the pickup-location dropdown (no auth needed). */
export async function getShippingZones(): Promise<{ zones: ShippingZone[] }> {
  const res = await axios.get<{ zones: ShippingZone[] }>(`${BASE_URL}/shipping/zones`);
  return res.data;
}

// ── public ──
export async function getRegistrationRequirements(): Promise<{ requirements: RequirementDef[] }> {
  const res = await axios.get<{ requirements: RequirementDef[] }>(
    `${BASE_URL}/merchant/registration-requirements`,
  );
  return res.data;
}

export async function registerMerchant(
  body: Record<string, unknown>,
): Promise<{ userId: string; status: string }> {
  const res = await axios.post<{ userId: string; status: string }>(
    `${BASE_URL}/merchant/register`,
    body,
    {
      withCredentials: true,
    },
  );
  return res.data;
}

// ── merchant self-service ──
export async function getMerchantMe(): Promise<MerchantMe> {
  return (await api.get<MerchantMe>('/merchant/me')).data;
}
export async function updateMerchantMe(body: Record<string, unknown>) {
  return (await api.patch('/merchant/me', body)).data;
}
export async function submitTextRequirement(requirementKey: string, textValue: string) {
  return (await api.post('/merchant/requirements', { requirementKey, textValue })).data;
}
export async function uploadFileRequirement(requirementKey: string, file: File) {
  const fd = new FormData();
  fd.append('requirementKey', requirementKey);
  fd.append('file', file);
  return (await api.post('/merchant/requirements/file', fd)).data;
}

export async function listProducts(): Promise<MerchantProduct[]> {
  return (await api.get<MerchantProduct[]>('/merchant/products')).data;
}
export async function createProduct(body: Record<string, unknown>) {
  return (await api.post('/merchant/products', body)).data;
}
export async function updateProduct(id: string, body: Record<string, unknown>) {
  return (await api.patch(`/merchant/products/${id}`, body)).data;
}
export async function setStock(id: string, qty: number) {
  return (await api.post(`/merchant/products/${id}/stock`, { qty })).data;
}
export async function addVariant(productId: string, body: MerchantVariantInput) {
  return (await api.post(`/merchant/products/${productId}/variants`, body)).data;
}
export async function updateVariant(
  productId: string,
  variantId: string,
  body: Partial<
    {
      name: string;
      sku: string;
      price: string;
      srp: string | null;
      attributes: Record<string, string>;
      active: boolean;
    } & MerchantVariantOverrides
  >,
) {
  return (await api.patch(`/merchant/products/${productId}/variants/${variantId}`, body)).data;
}
export async function setVariantStock(productId: string, variantId: string, qty: number) {
  return (await api.post(`/merchant/products/${productId}/variants/${variantId}/stock`, { qty }))
    .data;
}
export async function uploadProductImage(id: string, file: File): Promise<{ images: string[] }> {
  const fd = new FormData();
  fd.append('file', file);
  return (await api.post<{ images: string[] }>(`/merchant/products/${id}/images`, fd)).data;
}
/** Public (unauthenticated) URL for the Nth product image — served by GET /products/:id/image/:idx. */
export function productImageUrl(productId: string, idx: number): string {
  return `${BASE_URL}/products/${productId}/image/${idx}`;
}
export async function deleteProductImage(id: string, key: string): Promise<{ images: string[] }> {
  return (
    await api.delete<{ images: string[] }>(`/merchant/products/${id}/images`, { data: { key } })
  ).data;
}

// ── Merchant coupons (CMS) ──
export type MerchantCoupon = {
  id: string;
  code: string;
  merchantUserId: string | null;
  discountType: 'percent' | 'amount';
  discountValue: string;
  oneTime: boolean;
  maxUses: number | null;
  usedCount: number;
  perUserLimit: number | null;
  validFrom: string | null;
  validUntil: string | null;
  active: boolean;
  productIds: string[];
  createdAt: string;
  updatedAt: string;
};
export type MerchantCouponInput = {
  code?: string;
  discountType: 'percent' | 'amount';
  discountValue: string;
  oneTime?: boolean;
  maxUses?: number | null;
  perUserLimit?: number | null;
  validFrom?: string | null;
  validUntil?: string | null;
  active?: boolean;
  productIds: string[];
};
export async function listCoupons(): Promise<MerchantCoupon[]> {
  return (await api.get<MerchantCoupon[]>('/merchant/coupons')).data;
}
export async function createCoupon(body: MerchantCouponInput): Promise<MerchantCoupon> {
  return (await api.post<MerchantCoupon>('/merchant/coupons', body)).data;
}
export async function updateCoupon(
  id: string,
  body: Partial<MerchantCouponInput>,
): Promise<MerchantCoupon> {
  return (await api.patch<MerchantCoupon>(`/merchant/coupons/${id}`, body)).data;
}
export async function deleteCoupon(id: string): Promise<void> {
  await api.delete(`/merchant/coupons/${id}`);
}

export async function listOrders(status?: string): Promise<MerchantOrder[]> {
  return (await api.get<MerchantOrder[]>('/merchant/orders', { params: { status } })).data;
}
export async function getMerchantOrder(id: string): Promise<MerchantOrderDetail> {
  return (await api.get<MerchantOrderDetail>(`/merchant/orders/${id}`)).data;
}
export async function fulfillOrder(
  id: string,
  body: { status: string; trackingNumber?: string; note?: string },
) {
  return (await api.post(`/merchant/orders/${id}/fulfill`, body)).data;
}

export async function getWallet(): Promise<MerchantWallet> {
  return (await api.get<MerchantWallet>('/merchant/wallet')).data;
}
export async function getLedger(): Promise<MerchantLedgerRow[]> {
  return (await api.get<MerchantLedgerRow[]>('/merchant/wallet/ledger')).data;
}

export async function getPayoutMethods(): Promise<{ methods: PayoutMethod[] }> {
  return (await api.get<{ methods: PayoutMethod[] }>('/merchant/payouts/methods')).data;
}
export async function previewPayout(amount: string, method: string) {
  return (await api.post('/merchant/payouts/requests/preview', { amount, method })).data as {
    gross: string;
    serviceCharge: string;
    adminFee: string;
    withholdingTax: string;
    net: string;
    /** The deductions in the order the tenant's global policy applied them. */
    steps?: Array<{
      key: string;
      label: string;
      mode: 'percent' | 'flat';
      value: string;
      base: string;
      amount: string;
    }>;
  };
}
export async function requestPayout(body: {
  amount: string;
  method: string;
  payload?: Record<string, unknown>;
}) {
  return (await api.post('/merchant/payouts/requests', body)).data;
}
export async function listPayouts(): Promise<MerchantPayoutRequest[]> {
  return (await api.get<MerchantPayoutRequest[]>('/merchant/payouts/requests')).data;
}
export async function cancelPayout(id: string) {
  return (await api.post(`/merchant/payouts/requests/${id}/cancel`)).data;
}
